import type { Contact, ContactPublicKeyEntry } from "@/data/schemas";

type RawPublicKeyEntry = {
  publicKey?: string;
  public_key?: string;
  deviceId?: string;
  device_id?: string;
  mlKemPublicKey?: string;
  ml_kem_public_key?: string;
};

function normalizePublicKeyEntries(
  entries: unknown,
  fallbackDeviceId?: string
): ContactPublicKeyEntry[] {
  if (!Array.isArray(entries)) return [];
  return entries
    .map((entry: RawPublicKeyEntry) => {
      const publicKey = entry?.publicKey ?? entry?.public_key;
      if (!publicKey) return null;
      return {
        publicKey: String(publicKey).replace(/\s+/g, ""),
        deviceId: entry?.deviceId ?? entry?.device_id ?? fallbackDeviceId,
        mlKemPublicKey:
          entry?.mlKemPublicKey ?? entry?.ml_kem_public_key ?? undefined,
      } as ContactPublicKeyEntry;
    })
    .filter(Boolean) as ContactPublicKeyEntry[];
}

function parsePublicKeyPayload(
  rawPayload: unknown,
  fallbackDeviceId?: string
): ContactPublicKeyEntry[] {
  if (typeof rawPayload !== "string" || !rawPayload.trim()) return [];
  const trimmed = rawPayload.trim();
  if (trimmed.startsWith("[") && trimmed.endsWith("]")) {
    try {
      const parsed = JSON.parse(trimmed);
      return normalizePublicKeyEntries(parsed, fallbackDeviceId);
    } catch {
      return [];
    }
  }
  return normalizePublicKeyEntries([{ publicKey: trimmed, deviceId: fallbackDeviceId }], fallbackDeviceId);
}

export function extractContactPublicKeys(
  contact: Contact | null | undefined
): ContactPublicKeyEntry[] {
  if (!contact) return [];
  const legacyKeys =
    (contact as any).publicKeys ??
    (contact as any).public_keys ??
    undefined;
  const deviceId = (contact as any).deviceId ?? undefined;
  if (Array.isArray(legacyKeys) && legacyKeys.length > 0) {
    return normalizePublicKeyEntries(legacyKeys, deviceId);
  }
  const rawPayload = (contact as any).publicKey ?? (contact as any).public_key;
  return parsePublicKeyPayload(rawPayload, deviceId);
}

export function resolveContactPublicKey(
  contact: Contact | null | undefined,
  keyId?: string
): string | undefined {
  const keys = extractContactPublicKeys(contact);
  if (!keys.length) return undefined;

  if (keyId) {
    const match = keys.find((pk) => pk?.deviceId === keyId);
    if (match?.publicKey) return match.publicKey;
  }

  const deviceId = (contact as any)?.deviceId;
  if (deviceId) {
    const match = keys.find((pk) => pk?.deviceId === deviceId);
    if (match?.publicKey) return match.publicKey;
  }

  return keys[0]?.publicKey;
}

export function serializeContactPublicKeys(
  entries: ContactPublicKeyEntry[] | null | undefined,
  fallbackDeviceId?: string
): string | null {
  const normalized = normalizePublicKeyEntries(entries ?? [], fallbackDeviceId);
  if (!normalized.length) return null;
  return JSON.stringify(
    normalized.map((entry) => ({
      publicKey: entry.publicKey,
      deviceId: entry.deviceId,
      mlKemPublicKey: (entry as any).mlKemPublicKey ?? null,
    }))
  );
}
